import { useCallback } from 'react';
import {
  useCollectionsStore,
  setSchemas,
  setSelectedSchemaEntry,
  setSelectedRequest,
  setSelectedFolder,
  setSelectedCollection,
  setEditingSchemaItem,
} from '../store/collectionsStore';
import { useTabsStore, setSchemaTabs } from '../store/tabsStore';
import { useWorkspaceStore } from '../store/workspaceStore';
import { useAuthStore } from '../store/authStore';
import { useVariablesStore } from '../store/variablesStore';
import {
  createSchema as createSchemaInTree,
  createSchemaFolder as createSchemaFolderInTree,
  renameSchemaEntry,
  deleteSchemaEntry,
  duplicateSchemaItem,
  duplicateSchemaFolder,
  moveSchemaEntry,
  updateSchemaItem,
} from '../utils/schemaTree';
import { usesLocalDataPath, isGitWorkspace } from '../utils/workspaceStorage';
import { saveWorkspaceSnapshot } from '../services/workspaceStoreRouter';
import { saveCloudSchemas } from '../services/localWorkspaceStore';
import { writeMirror } from '../services/workspaceMirror';
import { syncEngineEnabled } from '../utils/featureFlags';

/**
 * Schema tree CRUD + schema-tab editing + schema persistence (cloud snapshot +
 * YAML mirror). Schemas live in collectionsStore; tabs in tabsStore. All domain
 * reads go through getState(). buildLiveSnapshot is returned so App's mirror
 * auto-write effect and brief-05 workspace code can share it — it reads via
 * getState() and is stable, so App's mirror effect MUST keep the underlying
 * values (collections/schemas/vars/environments/currentWorkspace) in its dep
 * array (it does) to keep auto-writing.
 *
 * @param {{
 *   openSchemaTab: Function,
 *   toastT: Function,
 *   snapshotHydratedRef: { current: any },
 *   renameSchemaSnapshotRef: { current: any },
 * }} deps
 */
export function useSchemas({
  openSchemaTab,
  toastT,
  snapshotHydratedRef,
  renameSchemaSnapshotRef,
}) {
  const handleRenameSchemaEntry = useCallback((kind, id, name) => {
    setSchemas((prev) => renameSchemaEntry(prev, kind, id, name));
    if (kind === 'schema') {
      setSchemaTabs((prev) =>
        prev.map((t) => (String(t.schemaId) === String(id) ? { ...t, name } : t))
      );
    }
  }, []);

  const handleSelectSchemaEntry = useCallback((entry) => {
    setSelectedSchemaEntry(entry);
    setSelectedRequest(null);
    setSelectedFolder(null);
    setSelectedCollection(null);
  }, []);

  const handleStartSchemaRename = useCallback((kind, item) => {
    renameSchemaSnapshotRef.current = {
      kind,
      id: item.id,
      previousName: item.name,
    };
    setEditingSchemaItem({ kind, id: item.id });
  }, [renameSchemaSnapshotRef]);

  const handleSaveSchemaRename = useCallback((kind, id, newName) => {
    const trimmed = newName.trim();
    renameSchemaSnapshotRef.current = null;
    setEditingSchemaItem(null);
    if (!trimmed) return;
    handleRenameSchemaEntry(kind, id, trimmed);
  }, [handleRenameSchemaEntry, renameSchemaSnapshotRef]);

  const handleCancelSchemaRename = useCallback(() => {
    renameSchemaSnapshotRef.current = null;
    setEditingSchemaItem(null);
  }, [renameSchemaSnapshotRef]);

  const handleCommitSchemaName = useCallback(
    (newName, previousNameFromEdit) => {
      const trimmed = (newName || '').trim();
      if (!trimmed) return;

      const { schemaTabs, activeSchemaTabId } = useTabsStore.getState();
      const tab = schemaTabs.find((t) => t.id === activeSchemaTabId);
      if (!tab?.schemaId) return;

      const previousName = previousNameFromEdit ?? tab.name ?? 'Schema';
      if (trimmed === previousName.trim()) return;

      handleRenameSchemaEntry('schema', tab.schemaId, trimmed);
    },
    [handleRenameSchemaEntry]
  );

  const handleDeleteSchemaEntry = useCallback((kind, id) => {
    setSchemas((prev) => deleteSchemaEntry(prev, kind, id));
    if (kind === 'schema') {
      setSchemaTabs((prev) => prev.filter((t) => String(t.schemaId) !== String(id)));
    }
  }, []);

  const handleMoveSchemaEntry = useCallback((payload) => {
    setSchemas((prev) => moveSchemaEntry(prev, payload));
  }, []);

  const buildLiveSnapshot = useCallback((overrides = {}) => {
    const currentWorkspace = useWorkspaceStore.getState().currentWorkspace;
    const { collections, schemas } = useCollectionsStore.getState();
    const { globalVariables, workspaceVariables, environments, selectedEnvironment } =
      useVariablesStore.getState();
    return {
      workspaceName: currentWorkspace?.name || '',
      collections,
      globalVariables,
      workspaceVariables,
      environments,
      schemas: overrides.schemas ?? schemas,
      selectedEnvironmentId: selectedEnvironment?.id || '',
    };
  }, []);

  /** Persist schemas to the workspace store (mirror is flushed by the writeMirror effect). */
  const persistSchemas = useCallback(
    async (nextSchemas) => {
      const currentWorkspace = useWorkspaceStore.getState().currentWorkspace;
      const isAuthenticated = useAuthStore.getState().isAuthenticated;
      if (!currentWorkspace?.id) return;
      const snapshot = buildLiveSnapshot({ schemas: nextSchemas });

      if (usesLocalDataPath(currentWorkspace, isAuthenticated)) {
        await saveWorkspaceSnapshot(currentWorkspace, snapshot, { skipEmptyArrays: false });
        snapshotHydratedRef.current = currentWorkspace.id;
      } else {
        // Cloud workspace: schemas are the only device-side state it keeps, and
        // they go to their own key. Writing them through saveSnapshot() used to
        // mint `local-workspaces/<cloudId>/` — a phantom local workspace that
        // the next account's list picked up as its own.
        saveCloudSchemas(currentWorkspace.id, nextSchemas);
      }
    },
    [buildLiveSnapshot, snapshotHydratedRef]
  );

  const flushSchemasMirror = useCallback(
    async (nextSchemas) => {
      const currentWorkspace = useWorkspaceStore.getState().currentWorkspace;
      if (!currentWorkspace?.id || isGitWorkspace(currentWorkspace)) return;
      // Phase 4: enrolled workspaces mirror from SQLite via gitFlush
      // (triggered by the save in persistSchemas) — skip the legacy write.
      if (syncEngineEnabled(currentWorkspace)) return;
      await writeMirror(currentWorkspace, buildLiveSnapshot({ schemas: nextSchemas }));
    },
    [buildLiveSnapshot]
  );

  const handleCreateSchema = useCallback((folderId = null) => {
    const schemas = useCollectionsStore.getState().schemas;
    const { schemas: next, item } = createSchemaInTree(schemas, { folder_id: folderId });
    setSchemas(next);
    openSchemaTab(item);
    handleSelectSchemaEntry({ kind: 'schema', id: item.id });
    // Persist immediately so a reload right after creating keeps the new schema
    // (don't rely solely on the debounced auto-save effect).
    persistSchemas(next).catch(() => {});
    flushSchemasMirror(next).catch(() => {});
  }, [openSchemaTab, handleSelectSchemaEntry, persistSchemas, flushSchemasMirror]);

  const handleCreateSchemaFolder = useCallback((parentId = null) => {
    const schemas = useCollectionsStore.getState().schemas;
    const { schemas: next, folder } = createSchemaFolderInTree(schemas, { parent_id: parentId });
    setSchemas(next);
    handleSelectSchemaEntry({ kind: 'folder', id: folder.id });
    persistSchemas(next).catch(() => {});
    flushSchemasMirror(next).catch(() => {});
  }, [handleSelectSchemaEntry, persistSchemas, flushSchemasMirror]);

  const handleDuplicateSchema = useCallback((kind, id) => {
    const schemas = useCollectionsStore.getState().schemas;
    const next =
      kind === 'folder'
        ? duplicateSchemaFolder(schemas, id)
        : duplicateSchemaItem(schemas, id);
    setSchemas(next);
    // Persist immediately so a reload right after duplicating keeps the copy
    // (mirrors handleCreateSchema; don't rely solely on the debounced auto-save).
    persistSchemas(next).catch(() => {});
    flushSchemasMirror(next).catch(() => {});
  }, [persistSchemas, flushSchemasMirror]);

  const updateCurrentSchemaTabStable = useCallback((updates) => {
    setSchemaTabs((prev) =>
      prev.map((tab) =>
        tab.id === useTabsStore.getState().activeSchemaTabId
          ? { ...tab, ...updates, hasUnsavedChanges: true }
          : tab
      )
    );
  }, []);

  const saveSchemaTab = useCallback(
    async (tabData) => {
      if (!tabData?.schemaId) return;
      let nextSchemas = null;
      setSchemas((prev) => {
        nextSchemas = updateSchemaItem(prev, tabData.schemaId, {
          name: tabData.name,
          rootType: tabData.rootType,
          nodes: tabData.nodes,
        });
        return nextSchemas;
      });
      setSchemaTabs((prev) =>
        prev.map((t) =>
          t.id === useTabsStore.getState().activeSchemaTabId
            ? {
                ...t,
                name: tabData.name,
                rootType: tabData.rootType,
                nodes: tabData.nodes,
                hasUnsavedChanges: false,
              }
            : t
        )
      );
      try {
        await persistSchemas(nextSchemas);
        await flushSchemasMirror(nextSchemas);
        toastT('toast.schemaSaved', 'success');
      } catch (error) {
        console.error('Failed to save schema:', error);
        toastT('toast.saveSchemaFailed', 'error', { message: error.message });
      }
    },
    [persistSchemas, flushSchemasMirror, toastT]
  );

  return {
    handleRenameSchemaEntry,
    handleSelectSchemaEntry,
    handleStartSchemaRename,
    handleSaveSchemaRename,
    handleCancelSchemaRename,
    handleCommitSchemaName,
    handleDeleteSchemaEntry,
    handleMoveSchemaEntry,
    buildLiveSnapshot,
    persistSchemas,
    flushSchemasMirror,
    handleCreateSchema,
    handleCreateSchemaFolder,
    handleDuplicateSchema,
    updateCurrentSchemaTabStable,
    saveSchemaTab,
  };
}
